import React from 'react'
import { Button, ColorPicker, InputNumber, Popover, Segmented, Slider, Tooltip } from 'antd'
import { BlockOutlined, DownOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons'
import { COLOR_COTA, GUIAS_LARGO_DEFECTO } from '../utils/cotizacionImagenUtils'

const COLORES = [
  { color: COLOR_COTA, nombre: 'Rojo' },
  { color: '#1f2328', nombre: 'Negro' },
  { color: '#ffffff', nombre: 'Blanco' },
  { color: '#1677ff', nombre: 'Azul' },
  { color: '#f5b700', nombre: 'Amarillo' },
  { color: '#16a34a', nombre: 'Verde' },
  { color: '#ea580c', nombre: 'Naranja' },
  { color: '#6b7280', nombre: 'Gris' },
]

// ==================== Iconos de vista previa ====================
const Icono = ({ children, rotar = false }) => (
  <svg
    width="26"
    height="16"
    viewBox="0 0 26 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={rotar ? { transform: 'rotate(-90deg)' } : undefined}
  >
    {children}
  </svg>
)

// Punta dibujada en el extremo izquierdo; `espejo` la pasa al derecho.
const IconoPunta = ({ tipo, espejo = false }) => (
  <Icono>
    <g transform={espejo ? 'translate(26 0) scale(-1 1)' : undefined}>
      {tipo === 'flecha' && (
        <>
          <line x1="5" y1="8" x2="23" y2="8" />
          <polygon points="3,8 9,4.5 9,11.5" fill="currentColor" stroke="none" />
        </>
      )}
      {tipo === 'redonda' && (
        <>
          <line x1="6" y1="8" x2="23" y2="8" />
          <circle cx="5" cy="8" r="2.6" fill="currentColor" stroke="none" />
        </>
      )}
      {tipo === 'ninguna' && <line x1="3" y1="8" x2="23" y2="8" />}
    </g>
  </Icono>
)

const IconoLetra = ({ arriba, rotar }) => (
  <Icono rotar={rotar}>
    <line x1="3" y1={arriba ? 12 : 4} x2="23" y2={arriba ? 12 : 4} />
    <text
      x="13"
      y={arriba ? 6.5 : 13.5}
      fontSize="8"
      fontWeight="800"
      textAnchor="middle"
      dominantBaseline="middle"
      fill="currentColor"
      stroke="none"
    >
      A
    </text>
  </Icono>
)

const IconoGuias = ({ direccion, rotar }) => {
  const [y1, y2] = { arriba: [1.5, 8], abajo: [8, 14.5], ambos: [1.5, 14.5] }[direccion]
  return (
    <Icono rotar={rotar}>
      <line x1="5" y1="8" x2="21" y2="8" />
      <line x1="5" y1={y1} x2="5" y2={y2} strokeWidth="1.2" strokeDasharray="2 1.6" />
      <line x1="21" y1={y1} x2="21" y2={y2} strokeWidth="1.2" strokeDasharray="2 1.6" />
    </Icono>
  )
}

const IconoTipo = ({ tipo }) => (
  <Icono>
    {tipo === 'medida' ? (
      <>
        <line x1="4" y1="11" x2="22" y2="11" />
        <polygon points="2,11 6,8.8 6,13.2" fill="currentColor" stroke="none" />
        <polygon points="24,11 20,8.8 20,13.2" fill="currentColor" stroke="none" />
        <text x="13" y="5" fontSize="7" fontWeight="800" textAnchor="middle" dominantBaseline="middle" fill="currentColor" stroke="none">A</text>
      </>
    ) : (
      <>
        <circle cx="7" cy="6" r="4.6" />
        <text x="7" y="6.4" fontSize="6" fontWeight="800" textAnchor="middle" dominantBaseline="middle" fill="currentColor" stroke="none">A</text>
        <polyline points="11.6,6 20,6 20,14" />
        <polygon points="20,15.5 17.8,11.5 22.2,11.5" fill="currentColor" stroke="none" />
      </>
    )}
  </Icono>
)

const IconoLineaGlobo = ({ forma }) => (
  <Icono>
    <circle cx="6" cy="5" r="3.6" />
    {forma === 'codo' ? <polyline points="9.6,5 21,5 21,14" /> : <line x1="8.8" y1="7.2" x2="21" y2="14" />}
  </Icono>
)

const opcionesPunta = (espejo) =>
  [
    ['flecha', 'Flecha'],
    ['redonda', 'Redonda'],
    ['ninguna', 'Sin punta'],
  ].map(([value, nombre]) => ({
    value,
    label: (
      <Tooltip title={nombre}>
        <span className="cotz-estilo__icono"><IconoPunta tipo={value} espejo={espejo} /></span>
      </Tooltip>
    ),
  }))

// ==================== Piezas ====================
const Grupo = ({ titulo, children }) => (
  <div className="cotz-estilo__grupo">
    <span className="cotz-estilo__etiqueta">{titulo}</span>
    <div className="cotz-estilo__controles">{children}</div>
  </div>
)

const BotonPopover = ({ contenido, titulo, children, disabled }) => (
  <Popover content={contenido} title={titulo} trigger="click" placement="bottom" arrow={false}>
    <Button size="small" className="cotz-estilo__boton" disabled={disabled}>
      {children}
      <DownOutlined className="cotz-estilo__caret" />
    </Button>
  </Popover>
)

// Slider + número. El historial se guarda una vez al tomar el slider.
const Rango = ({ valor, min, max, step, sufijo, onCambio, onInicio }) => (
  <div className="cotz-estilo__rango" onPointerDown={onInicio}>
    <Slider min={min} max={max} step={step} value={valor} tooltip={{ open: false }} onChange={(v) => onCambio(v, false)} />
    <InputNumber
      size="small"
      min={min}
      max={max}
      step={step}
      value={valor}
      suffix={sufijo}
      onChange={(v) => v != null && onCambio(v, true)}
    />
  </div>
)

/**
 * Barra contextual de estilo de la cota seleccionada. Siempre se renderiza
 * (deshabilitada sin selección) para que el lienzo no cambie de tamaño.
 *
 * `estilo` ya viene combinado con los valores por defecto. `onCambio(cambios,
 * conHistorial)` aplica cambios a la cota; `onGuardarHistorial` se llama al
 * empezar a arrastrar un slider.
 */
const BarraEstiloCota = ({
  letra,
  estilo,
  vertical,
  onCambio,
  onGuardarHistorial,
  onRestablecer,
  onAplicarATodas,
}) => {
  const inactiva = !letra
  // Lado de la letra: 1 = arriba (izquierda si la cota es vertical).
  const lado = estilo.lado || 1
  // Guías: 1 = arriba/izquierda, -1 = abajo/derecha, 0 = ambos lados.
  // Sin elegir, van al lado contrario de la letra (hacia la pieza).
  const guiasLado = estilo.guiasLado ?? -lado
  const direccionGuias = guiasLado === 0 ? 'ambos' : guiasLado > 0 ? 'arriba' : 'abajo'
  const nombreLado = (arriba) => (vertical ? (arriba ? 'Izquierda' : 'Derecha') : arriba ? 'Arriba' : 'Abajo')
  const guiasActivas = estilo.guias > 0
  const esGlobo = estilo.tipo === 'globo'

  const contenidoColor = (
    <div className="cotz-estilo__pop">
      <div className="cotz-estilo__paleta">
        {COLORES.map(({ color, nombre }) => (
          <Tooltip key={color} title={nombre}>
            <button
              type="button"
              className={`cotz-estilo__color ${estilo.color === color ? 'is-activo' : ''}`}
              style={{ background: color }}
              onClick={() => onCambio({ color })}
            />
          </Tooltip>
        ))}
      </div>
      <div className="cotz-estilo__fila">
        <span>Personalizado</span>
        <ColorPicker
          size="small"
          disabledAlpha
          showText
          value={estilo.color}
          getPopupContainer={(nodo) => nodo.parentElement}
          onChangeComplete={(c) => onCambio({ color: c.toHexString() })}
        />
      </div>
    </div>
  )

  const contenidoGrosor = (
    <div className="cotz-estilo__pop">
      <Rango
        valor={estilo.grosor}
        min={0.5}
        max={3}
        step={0.25}
        sufijo="×"
        onCambio={(grosor, h) => onCambio({ grosor }, h)}
        onInicio={onGuardarHistorial}
      />
    </div>
  )

  const contenidoLetra = (
    <div className="cotz-estilo__pop">
      <div className="cotz-estilo__fila"><span>Tamaño</span></div>
      <Rango
        valor={estilo.letraTamano}
        min={0.5}
        max={2.5}
        step={0.1}
        sufijo="×"
        onCambio={(letraTamano, h) => onCambio({ letraTamano }, h)}
        onInicio={onGuardarHistorial}
      />
    </div>
  )

  const contenidoGuias = (
    <div className="cotz-estilo__pop">
      <div className="cotz-estilo__fila"><span>Largo</span></div>
      <Rango
        valor={estilo.guias}
        min={20}
        max={800}
        step={10}
        onCambio={(guias, h) => onCambio({ guias }, h)}
        onInicio={onGuardarHistorial}
      />
      <div className="cotz-estilo__fila">
        <span>Trazo</span>
        <Segmented
          size="small"
          value={estilo.guiasEstilo}
          onChange={(guiasEstilo) => onCambio({ guiasEstilo })}
          options={[
            { value: 'discontinua', label: 'Discontinuo' },
            { value: 'continua', label: 'Continuo' },
          ]}
        />
      </div>
    </div>
  )

  return (
    <div className={`cotz-estilo ${inactiva ? 'is-inactiva' : ''}`}>
      <div className="cotz-estilo__cota">
        {inactiva ? (
          <span className="cotz-estilo__vacio">Selecciona una cota para editar su estilo</span>
        ) : (
          <>
            <span className="cotz-estilo__letra" style={{ color: estilo.color }}>{letra}</span>
            <span className="cotz-estilo__subtitulo">Estilo de cota</span>
          </>
        )}
      </div>

      <Grupo titulo="Tipo">
        <Segmented
          size="small"
          disabled={inactiva}
          value={estilo.tipo}
          onChange={(tipo) => onCambio({ tipo })}
          options={[
            ['medida', 'Medida: línea con la letra al lado'],
            ['globo', 'Globo: letra en un círculo que señala la pieza'],
          ].map(([value, ayuda]) => ({
            value,
            label: (
              <Tooltip title={ayuda}>
                <span className="cotz-estilo__icono"><IconoTipo tipo={value} /></span>
              </Tooltip>
            ),
          }))}
        />
      </Grupo>

      <Grupo titulo="Color">
        <BotonPopover contenido={contenidoColor} disabled={inactiva}>
          <span className="cotz-estilo__muestra" style={{ background: estilo.color }} />
        </BotonPopover>
      </Grupo>

      <Grupo titulo="Grosor">
        <BotonPopover contenido={contenidoGrosor} disabled={inactiva}>
          <svg width="22" height="16" viewBox="0 0 22 16">
            <line x1="2" y1="8" x2="20" y2="8" stroke="currentColor" strokeWidth={1 + estilo.grosor * 1.4} strokeLinecap="round" />
          </svg>
          <span className="cotz-estilo__valor">{estilo.grosor}×</span>
        </BotonPopover>
      </Grupo>

      {esGlobo ? (
        <Grupo titulo="Línea · punta">
          <Segmented
            size="small"
            disabled={inactiva}
            value={estilo.lineaGlobo}
            onChange={(lineaGlobo) => onCambio({ lineaGlobo })}
            options={[
              ['codo', 'En codo (horizontal y luego vertical)'],
              ['recta', 'Recta'],
            ].map(([value, ayuda]) => ({
              value,
              label: (
                <Tooltip title={ayuda}>
                  <span className="cotz-estilo__icono"><IconoLineaGlobo forma={value} /></span>
                </Tooltip>
              ),
            }))}
          />
          <Segmented
            size="small"
            disabled={inactiva}
            value={estilo.puntaGlobo}
            onChange={(puntaGlobo) => onCambio({ puntaGlobo })}
            options={opcionesPunta(true)}
          />
        </Grupo>
      ) : (
        <Grupo titulo={vertical ? 'Punta arriba · abajo' : 'Punta izquierda · derecha'}>
          <Segmented
            size="small"
            disabled={inactiva}
            value={estilo.puntaA}
            onChange={(puntaA) => onCambio({ puntaA })}
            options={opcionesPunta(false)}
          />
          <Segmented
            size="small"
            disabled={inactiva}
            value={estilo.puntaB}
            onChange={(puntaB) => onCambio({ puntaB })}
            options={opcionesPunta(true)}
          />
        </Grupo>
      )}

      <Grupo titulo="Letra">
        {!esGlobo && (
        <Segmented
          size="small"
          disabled={inactiva}
          value={lado}
          // Las guías activas se quedan donde están aunque la letra cambie de lado.
          onChange={(nuevo) => onCambio(guiasActivas ? { lado: nuevo, guiasLado } : { lado: nuevo })}
          options={[1, -1].map((v) => ({
            value: v,
            label: (
              <Tooltip title={`Letra ${nombreLado(v > 0).toLowerCase()}`}>
                <span className="cotz-estilo__icono"><IconoLetra arriba={v > 0} rotar={vertical} /></span>
              </Tooltip>
            ),
          }))}
        />
        )}
        <Popover content={contenidoLetra} trigger="click" placement="bottom" arrow={false}>
          <Tooltip title="Tamaño de la letra">
            <Button size="small" icon={<SettingOutlined />} disabled={inactiva} />
          </Tooltip>
        </Popover>
      </Grupo>

      {!esGlobo && (
      <Grupo titulo="Líneas guía">
        <Segmented
          size="small"
          disabled={inactiva}
          value={guiasActivas ? direccionGuias : 'no'}
          onChange={(v) =>
            onCambio(
              v === 'no'
                ? { guias: 0 }
                : {
                    guias: guiasActivas ? estilo.guias : GUIAS_LARGO_DEFECTO,
                    guiasLado: { arriba: 1, abajo: -1, ambos: 0 }[v],
                  },
            )
          }
          options={[
            { value: 'no', label: <Tooltip title="Sin líneas guía"><span className="cotz-estilo__texto">No</span></Tooltip> },
            ...['arriba', 'abajo', 'ambos'].map((d) => ({
              value: d,
              label: (
                <Tooltip title={d === 'ambos' ? 'Guías a ambos lados' : `Guías hacia ${nombreLado(d === 'arriba').toLowerCase()}`}>
                  <span className="cotz-estilo__icono"><IconoGuias direccion={d} rotar={vertical} /></span>
                </Tooltip>
              ),
            })),
          ]}
        />
        <Popover content={contenidoGuias} trigger="click" placement="bottom" arrow={false}>
          <Tooltip title="Largo y trazo de las guías">
            <Button size="small" icon={<SettingOutlined />} disabled={inactiva || !guiasActivas} />
          </Tooltip>
        </Popover>
      </Grupo>
      )}

      <div className="cotz-estilo__acciones">
        <Tooltip title="Aplicar este estilo a todas las cotas de todas las fotos">
          <Button size="small" icon={<BlockOutlined />} disabled={inactiva} onClick={onAplicarATodas}>
            Aplicar a todas
          </Button>
        </Tooltip>
        <Tooltip title="Volver al estilo por defecto (rojo, flechas, sin guías)">
          <Button size="small" icon={<ReloadOutlined />} disabled={inactiva} onClick={onRestablecer} />
        </Tooltip>
      </div>
    </div>
  )
}

export default BarraEstiloCota
